import { SKILLS, SKILL_BY_ID, type SkillNode } from '../data/skills';
import { COIN_UPGRADES, upgradeCost, upgradeLevel, type CoinUpgradeId } from '../data/coinUpgrades';
import type { Game } from '../core/Game';

type Page = 'milestones' | 'coins' | 'research';
interface Card { row: HTMLElement; status: HTMLElement; bonus?: HTMLElement; next?: HTMLElement; buy: HTMLButtonElement; pin?: HTMLButtonElement; id: string; coin: boolean; }
export class SkillTree {
  private root: HTMLElement | null = null;
  private page: Page = 'milestones';
  private previousFocus: HTMLElement | null = null;
  private cards: Card[] = [];
  private confirmation: { id: string; coin: boolean; level: number } | null = null;
  private overlay: HTMLElement | null = null;
  constructor(private game: Game) {}
  get isOpen(): boolean { return !!this.root; }
  open(): void { this.openPage('milestones'); }
  openResearch(): void { this.openPage('research'); }
  back(): void { if (this.confirmation) this.cancelConfirmation(); else this.close(); }
  close(): void {
    if (!this.root) return;
    this.root.remove(); this.root = null; this.cards = []; this.overlay = null; this.confirmation = null;
    if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
  }
  private openPage(page: Page): void {
    if (!this.root) {
      this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.root = document.createElement('section'); this.root.className = 'ledger-backdrop';
      this.root.setAttribute('role', 'dialog'); this.root.setAttribute('aria-modal', 'true');
      this.root.addEventListener('pointerdown', event => {
        if (event.target !== this.root) return;
        event.preventDefault(); event.stopImmediatePropagation();
        if (this.confirmation) this.cancelConfirmation(); else this.close();
      });
      this.root.addEventListener('keydown', event => {
        if (event.code === 'Escape' || event.code === 'KeyK' || event.code === 'KeyE') {
          event.preventDefault(); event.stopPropagation();
          if (this.confirmation) this.cancelConfirmation(); else this.close();
        }
        if (event.code === 'Tab') {
          const scope = this.overlay ?? this.root;
          const buttons = Array.from(scope?.querySelectorAll<HTMLElement>('button:not(:disabled)') ?? []).filter(b => b.offsetParent !== null);
          const first = buttons[0], last = buttons[buttons.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
      });
      document.body.appendChild(this.root);
    }
    this.page = page; this.cards = []; this.overlay = null; this.confirmation = null;
    this.root.classList.toggle('research-ledger', page === 'research');
    this.root.setAttribute('aria-label', page === 'research' ? 'Cosmic Research' : 'The Growing Farm');
    this.root.innerHTML = `<article class="ledger-book"><header class="ledger-header"><div><small>CHEESE EMPIRE · PROGRESSION</small><h1>${page === 'research' ? 'Cosmic Research' : 'The Growing Farm'}</h1></div><div class="ledger-wallet"></div><button class="btn skill-close">Back to farm <span class="kbd">Esc</span></button></header><nav class="ledger-tabs"></nav><main class="ledger-content"></main><footer class="ledger-footer">Time running · Employees keep working while you plan.</footer></article>`;
    this.root.querySelector<HTMLButtonElement>('.skill-close')!.onclick = () => this.close();
    const nav = this.root.querySelector<HTMLElement>('.ledger-tabs')!;
    for (const [id, label] of (page === 'research' ? [['milestones', 'View Growing Farm']] : [['milestones', 'Farm Milestones'], ['coins', 'Coin Upgrades'], ['research', 'View Cosmic Research']]) as [Page, string][]) {
      const b = document.createElement('button'); b.className = 'btn'; b.textContent = label;
      b.setAttribute('aria-current', id === page ? 'page' : 'false');
      b.onclick = () => this.openPage(id); nav.appendChild(b);
    }
    const content = this.root.querySelector<HTMLElement>('.ledger-content')!;
    if (page === 'coins') {
      content.innerHTML = '<p>Choose improvements that suit your farm. Each has fifteen levels. Bonuses shown are totals, not compounding.</p><div class="upgrade-ledger"></div>';
      const list = content.querySelector<HTMLElement>('.upgrade-ledger')!;
      for (const [chapter, ids] of [['Craft & Care', COIN_UPGRADES.slice(0,5)], ['Commerce', COIN_UPGRADES.slice(5,6)], ['Cellar & Crew', COIN_UPGRADES.slice(6)]] as const) {
        const heading = document.createElement('h2'); heading.textContent = chapter; list.appendChild(heading);
        for (const u of ids) {
          const card = this.card(list, u.id, u.name, u.description, true);
          card.bonus = document.createElement('small'); card.next = document.createElement('small');
          card.row.querySelector('.ledger-copy')!.append(card.bonus, card.next);
          card.pin = document.createElement('button'); card.pin.className = 'btn small';
          card.pin.onclick = () => this.game.pinUpgrade(this.game.state.pinnedUpgradeId === u.id ? null : u.id);
          card.row.querySelector('.ledger-actions')!.appendChild(card.pin);
        }
      }
      const note = document.createElement('p'); note.className = 'legacy-bonuses';
      note.textContent = [this.game.hasSkill('steady_hands_1') ? 'Legacy bonus: +10% timing tolerance.' : '', this.game.hasSkill('market_negotiation_1') ? 'Legacy bonus: +5% sale negotiation.' : ''].filter(Boolean).join(' '); content.appendChild(note);
    } else if (page === 'research') {
      content.innerHTML = '<p>Spend gems earned from quizzes and actual sale tips. Research is purchased once; coin upgrades live in the Growing Farm.</p>';
      for (const n of SKILLS.filter(n => n.functional && n.currency === 'gems')) this.skillCard(content, n);
    } else {
      content.innerHTML = '<p>Starting Farm → Shop → Cellar → Expand the Farm. Employees and Field are separate choices after Cellar.</p><div class="ledger-row done"><div class="ledger-copy"><h2>1. Starting Farm</h2><small>Barn + Creamery · Owned from the beginning</small></div></div>';
      this.skillCard(content, SKILL_BY_ID.quality_feed_supplier);
      for (const [id, label] of [['shop','2. Shop'],['cellar','3. Cellar']] as const) this.skillCard(content, SKILL_BY_ID[id], label);
      const title = document.createElement('h2'); title.textContent = '4. Expand the Farm';content.appendChild(title);
      const choices = document.createElement('div');choices.className = 'ledger-choices';content.appendChild(choices);
      this.skillCard(choices, SKILL_BY_ID.employees); this.skillCard(choices, SKILL_BY_ID.field);
      const learning = document.createElement('p');learning.className='ledger-learning';content.appendChild(learning);
    }
    this.update(); this.root.querySelector<HTMLButtonElement>('.skill-close')?.focus({preventScroll:true});
  }
  private card(parent:HTMLElement,id:string,name:string,description:string,coin:boolean):Card {
    const row=document.createElement('section');row.className='ledger-row';row.dataset.upgrade=id;
    row.innerHTML='<div class="ledger-copy"><h3></h3><p></p><small class="ledger-status" role="status"></small></div><div class="ledger-actions"><button class="btn primary buy-upgrade"></button></div>';
    row.querySelector('h3')!.textContent=name;row.querySelector('p')!.textContent=description;
    const buy=row.querySelector<HTMLButtonElement>('.buy-upgrade')!;
    const card={row,status:row.querySelector<HTMLElement>('.ledger-status')!,buy,id,coin};
    buy.onclick=()=>this.confirm(card);parent.appendChild(row);this.cards.push(card);return card;
  }
  private skillCard(parent:HTMLElement,n:SkillNode,label=n.name):void {
    const c=this.card(parent,n.id,label,n.description,false);
    const requirements=document.createElement('small');
    requirements.textContent=n.requires.length?`Requires: ${n.requires.map(id=>SKILL_BY_ID[id]?.name??id).join(', ')}`:'Starting farm';
    c.row.querySelector('.ledger-copy')!.appendChild(requirements);
  }
  update():void {
    if(!this.root)return;
    this.root.querySelector<HTMLElement>('.ledger-wallet')!.textContent=this.page==='research'?`Gems: ${this.game.state.gems}`:`Coins: ${this.game.state.gold.toFixed(2)}`;
    for(const c of this.cards){
      const level=c.coin?upgradeLevel(this.game.state,c.id as CoinUpgradeId):0;
      const owned=c.coin?level>=15:this.game.hasSkill(c.id);
      const blocked=c.coin?this.game.coinUpgradeBlock(c.id as CoinUpgradeId):this.game.canBuySkill(c.id);
      c.row.classList.toggle('done',owned);c.buy.disabled=owned||!!blocked;
      c.status.textContent=owned?(c.coin?'MAX — 15 / 15':'✓ Owned'):(blocked??'Available');
      if(c.coin){const u=COIN_UPGRADES.find(u=>u.id===c.id)!;
        c.bonus!.textContent=`Level ${level} / 15 · ${u.bonusLabel(level)}`;
        c.next!.textContent=owned?'':`Next: ${u.bonusLabel(level+1)}`;
        c.buy.textContent=owned?'Maxed':`Upgrade · ${upgradeCost(u.id,level+1)} coins`;
        c.pin!.textContent=this.game.state.pinnedUpgradeId===c.id?'Unpin goal':'Pin goal';c.pin!.disabled=owned;
      }else{const n=SKILL_BY_ID[c.id];c.buy.textContent=owned?'Owned':`Unlock · ${n.cost} ${n.currency==='gems'?'gems':'coins'}`;}
    }
    const learning=this.root.querySelector<HTMLElement>('.ledger-learning');
    if(learning){const l=this.game.state.learning;learning.textContent=`Employee requirements: ${l.madeCheese?'✓':'○'} Make cheese · ${l.agedCheese?'✓':'○'} Age cheese · ${l.soldCheese?'✓':'○'} Sell cheese yourself.`;}
    if(this.confirmation){const p=this.confirmation;const blocked=p.coin?this.game.coinUpgradeBlock(p.id as CoinUpgradeId):this.game.canBuySkill(p.id);
      const changed=p.coin&&upgradeLevel(this.game.state,p.id as CoinUpgradeId)!==p.level;
      this.overlay!.querySelector<HTMLButtonElement>('.confirm-purchase')!.disabled=!!blocked||changed;
      this.overlay!.querySelector<HTMLElement>('.confirm-status')!.textContent=changed?'The level changed. Reopen confirmation.':blocked??'';
    }
  }
  private confirm(c:Card):void {
    if(c.buy.disabled)return;this.cancelConfirmation();
    const level=c.coin?upgradeLevel(this.game.state,c.id as CoinUpgradeId):0;
    this.confirmation={id:c.id,coin:c.coin,level};
    const cost=c.coin?upgradeCost(c.id as CoinUpgradeId,level+1):SKILL_BY_ID[c.id].cost;
    const currency=c.coin?'coins':SKILL_BY_ID[c.id].currency==='gems'?'gems':'coins';
    this.overlay=document.createElement('div');this.overlay.className='ledger-confirm';
    this.overlay.setAttribute('role','alertdialog');this.overlay.setAttribute('aria-label','Confirm purchase');
    this.overlay.innerHTML='<div><h2>Confirm purchase</h2><p class="confirm-copy"></p><p class="confirm-status" role="status"></p><div class="row"><button class="btn primary confirm-purchase">Confirm purchase</button><button class="btn cancel-purchase">Cancel</button></div></div>';
    const name=c.coin?COIN_UPGRADES.find(u=>u.id===c.id)!.name:SKILL_BY_ID[c.id].name;
    this.overlay.querySelector<HTMLElement>('.confirm-copy')!.textContent=`${name}${c.coin?` level ${level+1}`:''} for ${cost} ${currency}? Remaining ${currency}: ${(this.game.state[currency==='gems'?'gems':'gold']-cost).toFixed(2)}.`;
    this.overlay.querySelector<HTMLButtonElement>('.cancel-purchase')!.onclick=()=>this.cancelConfirmation();
    this.overlay.querySelector<HTMLButtonElement>('.confirm-purchase')!.onclick=()=>{
      const p=this.confirmation;if(!p)return;
      if(p.coin&&upgradeLevel(this.game.state,p.id as CoinUpgradeId)!==p.level)return;
      if(p.coin)this.game.buyCoinUpgrade(p.id as CoinUpgradeId);else this.game.buySkill(p.id);
      this.cancelConfirmation();this.update();
    };
    this.overlay.addEventListener('pointerdown',e=>{if(e.target===this.overlay){e.preventDefault();e.stopPropagation();this.cancelConfirmation();}});
    this.root!.appendChild(this.overlay);this.update();this.overlay.querySelector<HTMLButtonElement>('.cancel-purchase')!.focus();
  }
  private cancelConfirmation():void {
    const id=this.confirmation?.id;this.overlay?.remove();this.overlay=null;this.confirmation=null;
    const buy = this.cards.find(c=>c.id===id)?.buy;
    if (buy && !buy.disabled) buy.focus({preventScroll:true});
    else this.root?.querySelector<HTMLButtonElement>('.skill-close')?.focus({preventScroll:true});
  }
}
